// GENERAL
import React from "react";
import { motion, AnimatePresence } from "framer-motion";

// COMPONENTS
import { BaseText, BaseContainer } from "../../../../../../components";

// UTILS
import { getFontAwesomeIcon, formatCurrency } from "../../../../../../utils";
import renderBadge from "../../helpers/renderBadge";

// TYPES
import { BundleHeaderProps } from "../../types";

const BundleHeader: React.FC<BundleHeaderProps> = ({
	kitIdVisible,
	kitIdPrefix,
	leaseBadgeText,
	currencySymbol,
	quantityText,
	bundlePrefix,
	bundleBadgeText,
	hoveredItemId,
	expandedItems,
	leaseSuffix,
	quantity,
	name,
	number,
	extPriceBundleLease,
	extPriceBundlePurchase,
	uuid,
	isNestedBundle,
	isRequired,
}) => {
	////// CONDITIONAL VARS/COMPS /////
	const isExpanded = expandedItems.includes(uuid);

	///// CLASS NAMES /////
	const bundleContainerClasses = `p-2.5 pb-0 w-full `;
	const bundleIconContainerClasses = `mr-5 bg-secondary-200 p-3 rounded-lg flex items-center justify-center h-full w-16 `;
	const bundleIconClasses = `text-3xl text-gray-700`;
	const bundleTitleContainerClasses = `flex flex-col text-left`;
	const bundleBadgeContainerClasses = `flex flex-row gap-2`;
	const chevronClasses = `text-xl transition-colors duration-200 ${
		hoveredItemId === uuid
			? "text-hyperlink"
			: isExpanded
			? "text-primary-800 hover:text-primary-hyperlink"
			: "text-gray-7"
	}`;

	const nestedBundleContainerClasses = `pb-0 rounded-lg w-full border-transparent`;
	const nestedBundleIconContainerClasses = `mr-5 bg-secondary-200 p-3 rounded-lg flex items-center justify-center h-full w-16 max-sm:w-9 max-sm:p-1`;
	const nestedBundleIconClasses = `text-3xl text-gray-700 max-sm:text-base`;
	const nestedBundleTitleContainerClasses = `flex flex-col text-left`;
	return (
		<BaseContainer
			columnContainerClasses={
				isNestedBundle
					? nestedBundleContainerClasses
					: bundleContainerClasses
			}
			columnShape="semi-rounded"
			borderColor="transparent"
		>
			<div className="flex justify-between items-center w-full">
				<div className="flex">
					<div
						className={
							isNestedBundle
								? nestedBundleIconContainerClasses
								: bundleIconContainerClasses
						}
					>
						<div
							className={
								isNestedBundle
									? nestedBundleIconClasses
									: bundleIconClasses
							}
						>
							{getFontAwesomeIcon(bundlePrefix, "light")}
						</div>
					</div>
					<div
						className={
							isNestedBundle
								? nestedBundleTitleContainerClasses
								: bundleTitleContainerClasses
						}
					>
						<div className={bundleBadgeContainerClasses}>
							{!isNestedBundle && isRequired
								? renderBadge("Required", "text-xs font-medium")
								: isNestedBundle &&
								  renderBadge("Add-on", "text-xs font-medium")}
							{renderBadge(
								bundleBadgeText as string,
								"text-xs font-medium"
							)}
							{extPriceBundleLease !== null &&
								Number(extPriceBundleLease) !== 0 && (
									<div className="flex flex-row gap-2">
										{renderBadge(
											leaseBadgeText as string,
											"text-xs font-medium"
										)}
									</div>
								)}
						</div>
						<BaseText
							text={name}
							size="sm:text-lg text-sm"
							color="text-gray-700"
							fontFamily="font-inter-medium font-medium"
							tag="h3"
							additionalClasses="py-0.5"
						/>
						{kitIdVisible && (
							<BaseText
								text={`${kitIdPrefix} ${number}`}
								size="sm:text-base text-sm"
								color="text-primary-text"
								tag="p"
								additionalClasses="mb-0.5"
								fontFamily="font-inter-regular"
							/>
						)}

						<div className="flex flex-row items-center gap-3">
							{
								<BaseText
									text={`${currencySymbol}${formatCurrency(
										Number(extPriceBundlePurchase) || "0"
									)}`}
									size="sm:text-base text-sm"
									color="text-navy-500"
									tag="p"
									fontFamily="font-inter-medium font-medium"
								/>
							}
							{Number(extPriceBundleLease) !== 0 &&
								extPriceBundleLease !== null && (
									<>
										<BaseText
											text={`+ ${currencySymbol}${formatCurrency(
												Number(extPriceBundleLease)
											)}${leaseSuffix} `}
											size="sm:text-base text-sm"
											color="text-navy-500"
											fontFamily="font-inter-medium font-medium"
											tag="p"
										/>
									</>
								)}

							<BaseText
								text={`${quantityText} ${quantity}`}
								size="sm:text-base text-sm"
								color="text-navy-500"
								fontFamily="font-inter-medium font-medium"
								tag="p"
							/>
						</div>
					</div>
				</div>
				<div className="flex items-center gap-14">
					{hoveredItemId === uuid && (
						<BaseText
							text={
								expandedItems.includes(uuid)
									? "Collapse"
									: "Expand"
							}
							size="sm:text-base text-sm"
							color="text-gray-7"
							fontFamily="font-medium"
							tag="p"
							additionalClasses="max-lg:hidden"
						/>
					)}
					<AnimatePresence>
						<motion.div
							className={chevronClasses}
							initial={{ rotate: 0 }}
							animate={{
								rotate: expandedItems.includes(uuid) ? 180 : 0,
							}}
							transition={{
								duration: 0.3,
								ease: "easeInOut",
							}}
						>
							{getFontAwesomeIcon("CircleChevronDown", "solid")}
						</motion.div>
					</AnimatePresence>
				</div>
			</div>
		</BaseContainer>
	);
};

export default BundleHeader;
